import { DatadogPagesRouter } from '@datadog/browser-rum-nextjs';
import { AdminTRPCProvider, AppScopeEnum, FeatureFlagControlPanelProvider } from '@newfront-insurance/admin-ui';
import { FallbackSuspense } from '@newfront-insurance/admin-ui/layout';
import { GeneralAppProviders } from '@newfront-insurance/app-providers';
import type { AppProps } from 'next/app';
import type { JSX } from 'react';

import { getNovaMountedPath } from '@/client/lib/nova-mount-path';
import { AuthProvider } from '@/client/providers/auth';
import { TRPCProvider } from '@/client/providers/trpc';
import { getConfig } from '@/config';

import '@newfront-insurance/tailwind-config/globals.css';
import 'katex/dist/katex.min.css';
import 'streamdown/styles.css';
import '../client/global.css';

const config = getConfig();

type NovaPageComponent = AppProps['Component'] & {
  requiresAuth?: boolean;
};

export default function App({ Component, pageProps }: AppProps): JSX.Element {
  const requiresAuth = (Component as NovaPageComponent).requiresAuth !== false;
  const adminTrpcBasePath = getNovaMountedPath('/api/admin-ui/trpc');
  const innerContent = (
    <FallbackSuspense>
      <DatadogPagesRouter />
      <Component {...pageProps} />
    </FallbackSuspense>
  );

  return (
    <GeneralAppProviders
      authProvider={AuthProvider}
      config={config}
      trpcProvider={TRPCProvider}
      useConditionalAuthProvider={false}
      analyticsAppName="nova"
      withLoginBoundary={requiresAuth}
    >
      {requiresAuth ? (
        <FeatureFlagControlPanelProvider appScope={AppScopeEnum.ADMIN_NAVIGATOR} authProvider={AuthProvider}>
          <AdminTRPCProvider authProvider={AuthProvider} basePath={adminTrpcBasePath}>
            {innerContent}
          </AdminTRPCProvider>
        </FeatureFlagControlPanelProvider>
      ) : (
        innerContent
      )}
    </GeneralAppProviders>
  );
}
